import { Resources } from 'constants/resources';

import { useMemo } from 'react';

import { Card } from '@mui/material';
import { useCreatePathWithQuery } from 'hooks/useCreatePathWithQuery';
import { useNavigateToRoot } from 'hooks/useNavigateToRoot';
import { Link } from 'react-router-dom';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs/Dialog/Dialog';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { Typography } from 'shared/mui/Typography';
import { BankOperation } from 'types/embedded/bankOperation';
import { convertReactAdminIdToHydraId } from 'utils/convertReactAdminIdToHydraId';

type Props = {
  record: BankOperation | undefined;
} & DialogProps;

export const RequestsBankOpsCreateSuccessDialog: React.FC<Props> = ({ record, ...restProps }) => {
  const { toRoot } = useNavigateToRoot();
  const { createPathWithQuery } = useCreatePathWithQuery();

  const onClose = () => {
    restProps.onClose();
    toRoot();
  };

  const to = useMemo(() => {
    if (!record) {
      return '';
    }

    const id = record?.related
      ? convertReactAdminIdToHydraId(record?.id, Resources.RequestBankOperations)
      : record?.id;

    return createPathWithQuery({
      resource: Resources.RequestBankOperations,
      id,
      type: 'show',
    });
  }, [record]);

  return (
    <Dialog {...restProps} width="640px">
      <Card>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title="Заявка на банковскую операцию успешно создана"
          titleSize="smallBold"
        />
        <CardContent>
          <Typography>
            Создание заявки на банковскую операцию успешно выполнено. Вы можете отправить запрос на
            согласование или вернуться к списку заявок для дальнейшей работы.
          </Typography>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            <CancelButton
              key="back-button"
              label="Вернуться в список"
              onClick={toRoot}
              variant="outlined"
            />,
            <Button
              color="primary"
              component={Link}
              key="btn-agreement"
              label="Просмотр заявки"
              to={to}
              variant="contained"
            />,
          ]}
        />
      </Card>
    </Dialog>
  );
};
